/*
 * HDS header scroll + color-scheme observer.
 *
 * Decoupled from Primavera's header-menu.js. Two concerns:
 *   1. Toggle .hds-header--scrolled based on scroll position. CSS handles
 *      the transparent/filled visual via :hover, [open], and --scrolled.
 *   2. Rewrite the pill's data-color-scheme so its text + background come
 *      from Primavera's color-scheme system:
 *        - Transparent (at top, no hover, no menu open): mirror the first
 *          section after the header so text inverts correctly over dark
 *          vs light banners.
 *        - Filled (scrolled, hovered, or megamenu open): restore the pill's
 *          configured scheme (stashed on data-default-color-scheme).
 */

(function () {
  'use strict';

  const HEADER_SELECTOR = '.hds-header';
  const PILL_SELECTOR = '.hds-header__pill';
  const SCHEME_ATTR = 'data-color-scheme';
  const DEFAULT_SCHEME_ATTR = 'data-default-color-scheme';
  const SCROLL_THRESHOLD = 4;

  function init() {
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) return;

    const pill = header.querySelector(PILL_SELECTOR);
    if (!pill) return;

    const firstSection = findFirstSectionAfterHeader(header);
    let hovered = false;

    applyState();

    window.addEventListener('scroll', applyState, { passive: true });
    header.addEventListener('mouseenter', () => {
      hovered = true;
      applyState();
    });
    header.addEventListener('mouseleave', () => {
      hovered = false;
      applyState();
    });

    if (firstSection) {
      const mo = new MutationObserver(applyState);
      mo.observe(firstSection, { attributes: true, attributeFilter: [SCHEME_ATTR] });
    }

    /* When any megamenu opens, restore the default scheme so the pill reads
       as solid white even when currently mirroring a dark banner.
       Scope to actual megamenu triggers (`.header__menu-item`) — a broader
       `[aria-expanded][aria-controls]` selector also matches the search/
       account toggle buttons and the search input, and those toggle on
       every keystroke (predictive-search updates aria-expanded as the user
       types), which re-fires applyState() per-keystroke. */
    const megamenuButtons = header.querySelectorAll('.header__menu-item[aria-expanded][aria-controls]');
    if (megamenuButtons.length) {
      const megamenuObserver = new MutationObserver(applyState);
      megamenuButtons.forEach((btn) => {
        megamenuObserver.observe(btn, { attributes: true, attributeFilter: ['aria-expanded'] });
      });
    }

    function isAnyMegamenuOpen() {
      return !!header.querySelector('[aria-expanded="true"] + [data-submenu="megamenu"]');
    }

    function applyState() {
      const scrolled = window.scrollY > SCROLL_THRESHOLD;
      const menuOpen = isAnyMegamenuOpen();
      const alwaysFilled = header.classList.contains('hds-header--always-filled');
      const filled = scrolled || hovered || menuOpen || alwaysFilled;
      header.classList.toggle('hds-header--scrolled', scrolled);

      const defaultScheme = pill.getAttribute(DEFAULT_SCHEME_ATTR) || '';
      if (filled && defaultScheme) {
        pill.setAttribute(SCHEME_ATTR, defaultScheme);
        return;
      }
      if (firstSection) {
        const targetScheme = firstSection.getAttribute(SCHEME_ATTR);
        if (targetScheme) {
          pill.setAttribute(SCHEME_ATTR, targetScheme);
          return;
        }
      }
      if (defaultScheme) {
        pill.setAttribute(SCHEME_ATTR, defaultScheme);
      }
    }
  }

  function findFirstSectionAfterHeader(header) {
    const headerGroup = header.closest('.shopify-section-group-header-group') || header;
    let node = headerGroup.nextElementSibling;
    while (node) {
      if (node.hasAttribute && node.hasAttribute(SCHEME_ATTR)) {
        return node;
      }
      if (node.querySelector) {
        const inner = node.querySelector(`[${SCHEME_ATTR}]`);
        if (inner) return inner;
      }
      node = node.nextElementSibling;
    }
    return null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
